import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import productCoverImage from 'utils/productCoverImage';
import { CloudinaryPicture } from 'components/rich';
import useModalContext from 'components/context/modal';

import { ProductDetailLink } from '../pageLinks';
import { isViewOnWallProduct } from '../../lib/viewOnWall.mjs';
import PdpIcon from './pdpIcon';
import ViewOnWallCard from './viewOnWallCard';

// Largest print side drawn at full scale (in inches). Bigger sizes shrink
// together so the proportions between them stay true.
const MAX_DRAWN_INCHES = 7;

const SpecTable = ({ specs }) => (
  <dl className="pdp-specs">
    {specs.map((spec) => (
      <div key={spec.label} className="pdp-specs-row">
        <dt>
          {spec.icon && <PdpIcon name={spec.icon} />}
          {spec.label}
        </dt>
        <dd>{spec.value}</dd>
      </div>
    ))}
  </dl>
);

SpecTable.propTypes = {
  specs: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const SizeChart = ({ sizes, footnote }) => {
  return (
    <div className="pdp-block">
      <h3 className="pdp-block-title">Size chart</h3>
      <div className="pdp-size-chart">
        <table>
          <thead>
            <tr>
              <th scope="col">Size</th>
              <th scope="col">Inches</th>
              <th scope="col">Centimeters</th>
            </tr>
          </thead>
          <tbody>
            {sizes.map((size) => (
              <tr key={size.label}>
                <th scope="row">{size.label}</th>
                <td>{`${size.width}x${size.height} in`}</td>
                <td>{size.cm}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footnote && <p className="pdp-footnote">{footnote}</p>}
    </div>
  );
};

SizeChart.propTypes = {
  sizes: PropTypes.arrayOf(PropTypes.object).isRequired,
  footnote: PropTypes.string,
};

// Prints on the stage row are drawn to scale against each other. With more
// sizes the row shows the first ones (content order) and the full set opens in
// a modal that wraps into rows at one shared scale.
const INLINE_SIZES = 3;

const ProportionsStage = ({ sizes }) => {
  const scale = Math.min(
    1,
    MAX_DRAWN_INCHES / Math.max(...sizes.map((size) => Math.max(size.width, size.height))),
  );

  // The stage fits the whole row, not just the largest print (_product-page.scss).
  const totalWidth = sizes.reduce((sum, size) => sum + size.width * scale, 0);
  // The modal wraps into rows, its columns fit the widest print.
  const maxWidth = Math.max(...sizes.map((size) => size.width * scale));

  return (
    <div
      className="pdp-proportions-stage"
      style={{ '--pdp-total-w': totalWidth, '--pdp-count': sizes.length, '--pdp-max-w': maxWidth }}
    >
      {sizes.map((size) => (
        <figure key={size.label} className="pdp-proportions-print">
          <div
            className={`pdp-proportions-paper${size.imageKey ? '' : ' pdp-proportions-paper--blank'}`}
            style={{ '--pdp-print-w': size.width * scale, '--pdp-print-h': size.height * scale }}
          >
            {size.imageKey && (
              <CloudinaryPicture
                prefix="products/"
                imageKey={size.imageKey}
                alt=""
                type="small"
                loading="lazy"
              />
            )}
          </div>
          <figcaption>
            <strong>{`${size.width}x${size.height} in`}</strong>
            <span>{size.cm}</span>
            {size.use && <span>{size.use}</span>}
          </figcaption>
        </figure>
      ))}
    </div>
  );
};

ProportionsStage.propTypes = {
  sizes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const ActualProportions = ({ sizes, guideUrl }) => {
  const modalContext = useModalContext();
  const hasMore = sizes.length > INLINE_SIZES;

  const handleOnShowAllClick = () => {
    modalContext.show(
      <div className="pdp-proportions-all">
        <ProportionsStage sizes={sizes} />
      </div>,
      { title: 'Actual proportions', className: 'mw-1024' },
    );
  };

  return (
    <div className="pdp-proportions">
      <h3 className="pdp-block-title pdp-proportions-title">Actual proportions</h3>
      <ProportionsStage sizes={hasMore ? sizes.slice(0, INLINE_SIZES) : sizes} />
      {hasMore && (
        <button
          type="button"
          className="pdp-arrow-link pdp-proportions-more"
          aria-haspopup="dialog"
          onClick={handleOnShowAllClick}
        >
          {`See all ${sizes.length} sizes`}
          <PdpIcon name="arrowRight" size={14} />
        </button>
      )}
      {guideUrl && (
        <p className="pdp-proportions-guide">
          Not sure what size?{' '}
          <Link href={guideUrl} className="pdp-arrow-link">
            Read the photo print sizes guide
            <PdpIcon name="arrowRight" size={14} />
          </Link>
        </p>
      )}
    </div>
  );
};

ActualProportions.propTypes = {
  sizes: PropTypes.arrayOf(PropTypes.object).isRequired,
  guideUrl: PropTypes.string,
};

// Linked product cards (image, name, short description, arrow link), also
// used by the out-of-stock notice.
export const PairCards = ({ items }) => (
  <div className="pdp-pairs">
    {items.map(({ product, content }) => (
      <ProductDetailLink key={product.id} productId={product.id}>
        <a className="pdp-pair">
          <span className="pdp-pair-image">
            <CloudinaryPicture
              prefix="products/"
              imageKey={productCoverImage(product)?.key}
              alt=""
              type="small"
              loading="lazy"
            />
          </span>
          <span className="pdp-pair-text">
            <span className="pdp-pair-name">{content.caption}</span>
            {content.shortDescription && (
              <span className="pdp-pair-desc">{content.shortDescription}</span>
            )}
            <span className="pdp-arrow-link">
              {`View ${content.caption}`}
              <PdpIcon name="arrowRight" size={14} />
            </span>
          </span>
        </a>
      </ProductDetailLink>
    ))}
  </div>
);

const pairItemsPropType = PropTypes.arrayOf(
  PropTypes.shape({ product: PropTypes.object, content: PropTypes.object }),
).isRequired;

PairCards.propTypes = {
  items: pairItemsPropType,
};

const PairsWellWith = ({ items }) => (
  <div className="pdp-block">
    <h3 className="pdp-block-title">Pairs well with</h3>
    <PairCards items={items} />
  </div>
);

PairsWellWith.propTypes = {
  items: pairItemsPropType,
};

/**
 * "Product details" section of the redesigned PDP: spec table, size chart,
 * actual proportions, View on Wall and linked products, each only when its
 * data exists.
 */
const ProductDetails = ({ productId, content, pairs }) => {
  const hasSizes = content.sizes?.length > 0;
  const offersViewOnWall = isViewOnWallProduct(productId);

  return (
    <div className="pdp-details">
      <div className="pdp-details-main">
        <SpecTable specs={content.specs} />
        {hasSizes && <SizeChart sizes={content.sizes} footnote={content.sizeChartFootnote} />}
        {pairs.length > 0 && <PairsWellWith items={pairs} />}
      </div>
      {(hasSizes || offersViewOnWall) && (
        <div className="pdp-details-side">
          {hasSizes && <ActualProportions sizes={content.sizes} guideUrl={content.sizeGuideUrl} />}
          {offersViewOnWall && <ViewOnWallCard productId={productId} />}
        </div>
      )}
    </div>
  );
};

ProductDetails.propTypes = {
  productId: PropTypes.string.isRequired,
  content: PropTypes.object.isRequired,
  pairs: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default ProductDetails;
